import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { MediaApiService } from '@simOn/common/media';
import { Attachment } from '@simOn/common/models';
import { PublicLinkService } from '@simOn/public-link';
import { publicLinkUnavailableTooltip, publicLinkUnsupportedTooltip } from '@simOn/public-link/models';
import { SimNewPropertyUploadImageComponent } from '@simOn/space/image/ui';
import { SimButtonModule } from '@simOn/ui/sim-button';
import { AssetsImage } from '@simOn/ui/sim-footer';
import { SimIconModule } from '@simOn/ui/sim-icon';
import { SimTooltipComponent } from '@simOn/ui/sim-tooltip';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { derivedAsync } from 'ngxtension/derived-async';
import { catchError, firstValueFrom, of } from 'rxjs';
import { SimSideMenuItemComponent } from '../sim-sidemenu-item/sim-sidemenu-item.component';

@Component({
  selector: 'layout-sidenav',
  templateUrl: './sim-sidenav.component.html',
  styleUrls: ['./sim-sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    SimButtonModule,
    RouterModule,
    SimNewPropertyUploadImageComponent,
    SimIconModule,
    SimTooltipComponent,
    SimSideMenuItemComponent
  ]
})
export class SimSidenavComponent {
  private readonly _oidcSecurityService = inject(OidcSecurityService);
  private readonly _mediaApiService = inject(MediaApiService);

  // Public link has no user account, so the cross-space "My spaces" list makes no sense there - the viewer is
  // scoped to the single shared space. Providers, Interior editor and Documents stay listed but disabled, so a
  // visitor sees what the product offers.
  private readonly _publicLinkService = inject(PublicLinkService);
  protected readonly isPublicModeActive = toSignal(this._publicLinkService.isPublicLinkModeActive$, {
    initialValue: false
  });
  /**
   * Visitors may open the calendar only when the link allows events (otherwise the entry is disabled with a
   * tooltip); logged users always. Reads the capabilities signal (not `allows()`), because a JWT link's
   * capabilities arrive after public mode is already on.
   */
  protected readonly calendarEnabled = computed(
    () => !this.isPublicModeActive() || this._publicLinkService.capabilities().canViewEvents
  );
  /** Calendar: the link could allow events. Providers, Interior editor, Documents: no public link offers them. */
  protected readonly publicLinkUnavailable = publicLinkUnavailableTooltip();
  protected readonly publicLinkUnsupported = publicLinkUnsupportedTooltip();

  readonly apartmentName = input.required<string>();
  readonly providersDisabled = input.required<boolean>();
  /** Without the "Documents View Only" privilege the Documents entry is disabled and explains why. */
  readonly canViewDocuments = input(true);
  /** The Stages entry is shown only when the space has a connected Stages project. */
  readonly stagesVisible = input(false);
  readonly apartmentAttachment = input<Attachment | undefined>();
  readonly interiorDesignerVisible = input.required<boolean>();
  readonly onClose = output<void>();
  readonly AssetsImage = AssetsImage;

  readonly photo = derivedAsync(() => {
    const attachment = this.apartmentAttachment();
    if (!attachment) return undefined;
    return this._mediaApiService
      .generateBlobUrlForImageDownload({
        url: attachment.blobPath,
        availableSizes: attachment.availableSizes,
        size: 'Medium'
      })
      .pipe(
        catchError((err) => {
          console.error(err);
          return of(undefined);
        })
      );
  });

  open = false;

  toggle() {
    this.open = !this.open;
  }

  closeSidenav() {
    this.open = false;
    this.onClose.emit();
  }

  logout(): void {
    firstValueFrom(this._oidcSecurityService.logoff());
  }
}
